// Desktop shell only: route clerk-js's Frontend API traffic through the Bun
// host's same-origin relay so Clerk's `__client` cookie becomes a FIRST-PARTY
// cookie of the app's own loopback origin.
//
// Without this the shell is stuck on a THIRD-PARTY cookie that WebKit refuses
// to store on a non-secure page, so every request to Clerk arrives as a brand
// new anonymous client and sign-in can never complete. The full mechanism, the
// measurements, and the guards live in src/clerkRelay.js.
//
// The host STATES whether the relay is available by stamping the HTML it
// serves (see clerkRelayMeta in src/server.js). We deliberately do not sniff
// for Tauri or for a loopback hostname: the same bundle is served by the Tauri
// shell, the Cocoa mac app, the Vite dev server and the hosted web app, and
// only the ones the Bun host serves have the relay mounted. Reading the host's
// own statement is exact where a guess would not be.
const META_NAME = "steerly-clerk-proxy";

// `pk_live_<base64>` / `pk_test_<base64>`; the payload decodes to the Frontend
// API host with a trailing `$`. Mirrors frontendApiFromPublishableKey in
// src/clerkRelay.js — the two sides of one contract, and there is no module a
// Bun-host CJS file and a Vite-bundled TS file can share.
export function frontendApiFromPublishableKey(publishableKey: string): string | null {
  const m = /^pk_(?:live|test)_(.+)$/.exec(publishableKey || "");
  if (!m) return null;
  try {
    const host = atob(m[1]).replace(/\$+$/, "").trim();
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host) ? host : null;
  } catch {
    return null;
  }
}

export function readClerkProxyUrl(doc: Document | undefined = globalThis.document): string | null {
  if (!doc) return null;
  const meta = doc.querySelector(`meta[name="${META_NAME}"]`);
  const content = meta?.getAttribute("content")?.trim();
  // Relative-only. `proxyUrl` is resolved against window.location.origin by
  // Clerk, which is what makes this work on whatever dynamic port the shell
  // ended up on — and refusing absolute values keeps a tampered document from
  // pointing Clerk at some other host.
  if (!content || !content.startsWith("/") || content.startsWith("//")) return null;
  return content;
}

export type ClerkProxyProps = {
  proxyUrl?: string;
};

// Setting `proxyUrl` also moves clerk-js's OWN script under the prefix —
// measured in WebKit, the page requests
// /__clerk/npm/@clerk/clerk-js@6/dist/clerk.browser.js. A `clerkJSUrl` prop
// pointing back at Clerk's CDN does NOT override that (tried it: the script
// request still went to the proxy path and 404'd, so Clerk never loaded and
// the app showed "Steerly can't reach sign-in"). The relay therefore serves
// /npm/* as well as /v1/* — see src/clerkRelay.js.
// The host names the Clerk instance its relay is bound to; we refuse the proxy
// unless it matches the key this bundle was built with. Relaying a bundle for
// one instance through another crosses `__client` namespaces in the shared
// loopback cookie jar and produces an auth failure with no readable cause.
// Falling back to talking to Clerk directly is the safer half of a bad choice,
// and the console line says why.
export function clerkProxyProps(
  publishableKey: string,
  doc: Document | undefined = globalThis.document
): ClerkProxyProps {
  const proxyUrl = readClerkProxyUrl(doc);
  if (!proxyUrl) return {};
  const stamped = doc
    ?.querySelector(`meta[name="${META_NAME}"]`)
    ?.getAttribute("data-frontend-api");
  const expected = frontendApiFromPublishableKey(publishableKey);
  // `stamped && stamped !== expected`, NOT `stamped && expected && …`: if the
  // host names an instance and we cannot confirm we match it — including
  // because our own key failed to parse — that is a reason to refuse, not to
  // wave it through.
  if (stamped && stamped !== expected) {
    console.error(
      `[clerk] host relay is bound to ${stamped} but this build uses ${expected}; ` +
        "not proxying. The host and the bundle disagree about the Clerk instance."
    );
    return {};
  }
  return { proxyUrl };
}
